"use client"

import { useState, useCallback, useMemo } from "react"
import { RefreshCw, Eye, EyeOff, Filter } from "lucide-react"
import { trackProductEvent } from "@/lib/analytics/client"

type Difficulty = "easy" | "medium" | "hard"

interface TriviaQuestion {
  question: string
  answer: string
  category: string
  difficulty: Difficulty
}

const triviaQuestions: TriviaQuestion[] = [
  // Geography
  { question: "What is the smallest country in the world by land area?", answer: "Vatican City, at just 0.44 square kilometers (0.17 square miles).", category: "Geography", difficulty: "easy" },
  { question: "What is the longest river in the world?", answer: "The Nile, stretching approximately 6,650 kilometers (4,130 miles) through northeastern Africa.", category: "Geography", difficulty: "easy" },
  { question: "Which country has the most natural lakes?", answer: "Canada, with over 879,000 lakes. That is more than all other countries combined.", category: "Geography", difficulty: "medium" },
  { question: "What is the driest inhabited continent on Earth?", answer: "Australia, with an average annual rainfall of just 470 mm.", category: "Geography", difficulty: "medium" },
  { question: "Which capital city is located at the highest altitude?", answer: "La Paz, Bolivia, sitting at approximately 3,640 meters (11,942 feet) above sea level.", category: "Geography", difficulty: "hard" },
  { question: "What is the only country that borders both the Atlantic and Indian Oceans?", answer: "South Africa, with coastlines on both the Atlantic (west) and Indian (east) Oceans.", category: "Geography", difficulty: "medium" },
  { question: "Which desert is the largest in the world?", answer: "The Antarctic Desert, covering about 14.2 million square kilometers. The Sahara is the largest hot desert.", category: "Geography", difficulty: "hard" },
  { question: "What is the most visited country in the world?", answer: "France, welcoming over 89 million international tourists annually.", category: "Geography", difficulty: "easy" },
  { question: "How many time zones does Russia span?", answer: "11 time zones, making it the country with the most time zones.", category: "Geography", difficulty: "medium" },
  { question: "Which African country has the largest population?", answer: "Nigeria, with over 220 million people.", category: "Geography", difficulty: "easy" },
  { question: "What is the deepest lake in the world?", answer: "Lake Baikal in Russia, reaching a maximum depth of 1,642 meters (5,387 feet).", category: "Geography", difficulty: "medium" },
  { question: "Which two countries share the longest international border?", answer: "Canada and the United States, with a border stretching 8,891 kilometers (5,525 miles).", category: "Geography", difficulty: "medium" },
  { question: "What is the only continent with no active volcanoes?", answer: "Australia, though it does have extinct volcanic formations.", category: "Geography", difficulty: "hard" },
  { question: "Which country is known as the Land of the Rising Sun?", answer: "Japan, because it lies to the east of the Asian mainland where the sun appears to rise.", category: "Geography", difficulty: "easy" },

  // Science
  { question: "What is the hardest natural substance on Earth?", answer: "Diamond, scoring a perfect 10 on the Mohs hardness scale.", category: "Science", difficulty: "easy" },
  { question: "How long does it take for light from the Sun to reach Earth?", answer: "Approximately 8 minutes and 20 seconds, traveling at about 299,792 km/s.", category: "Science", difficulty: "easy" },
  { question: "What is the most abundant gas in Earth's atmosphere?", answer: "Nitrogen, making up about 78% of the atmosphere.", category: "Science", difficulty: "easy" },
  { question: "How many bones does an adult human body have?", answer: "206 bones. Babies are born with about 270, but many fuse together as they grow.", category: "Science", difficulty: "easy" },
  { question: "What element has the chemical symbol 'Au'?", answer: "Gold. The symbol comes from the Latin word 'aurum.'", category: "Science", difficulty: "medium" },
  { question: "What is the speed of sound in air at sea level?", answer: "Approximately 343 meters per second (1,235 km/h or 767 mph).", category: "Science", difficulty: "medium" },
  { question: "What is the largest organ in the human body?", answer: "The skin, which covers an area of about 1.5 to 2 square meters in adults.", category: "Science", difficulty: "easy" },
  { question: "How many chromosomes do humans have?", answer: "46 chromosomes, arranged in 23 pairs.", category: "Science", difficulty: "medium" },
  { question: "What planet in our solar system has the most moons?", answer: "Saturn, with over 140 confirmed moons as of recent discoveries.", category: "Science", difficulty: "medium" },
  { question: "What is the chemical formula for table salt?", answer: "NaCl, or sodium chloride.", category: "Science", difficulty: "easy" },
  { question: "What is the rarest naturally occurring element in Earth's crust?", answer: "Astatine, with less than 30 grams estimated to exist in Earth's crust at any time.", category: "Science", difficulty: "hard" },
  { question: "At what temperature are Celsius and Fahrenheit equal?", answer: "-40 degrees. Both scales intersect at -40°.", category: "Science", difficulty: "hard" },
  { question: "What is the only metal that is liquid at standard room temperature?", answer: "Mercury (Hg), which melts at -38.83°C (-37.89°F).", category: "Science", difficulty: "medium" },
  { question: "How old is the Earth, approximately?", answer: "About 4.54 billion years old, based on radiometric dating of meteorites.", category: "Science", difficulty: "medium" },

  // History
  { question: "In what year did the Berlin Wall fall?", answer: "1989. The wall fell on November 9, 1989, after 28 years of dividing East and West Berlin.", category: "History", difficulty: "easy" },
  { question: "Who was the first person to walk on the Moon?", answer: "Neil Armstrong, on July 20, 1969, during the Apollo 11 mission.", category: "History", difficulty: "easy" },
  { question: "Which ancient civilization built Machu Picchu?", answer: "The Inca Empire, built around 1450 AD in modern-day Peru.", category: "History", difficulty: "easy" },
  { question: "What was the shortest war in recorded history?", answer: "The Anglo-Zanzibar War of 1896, lasting between 38 and 45 minutes.", category: "History", difficulty: "hard" },
  { question: "In what year did World War I begin?", answer: "1914, triggered by the assassination of Archduke Franz Ferdinand of Austria.", category: "History", difficulty: "easy" },
  { question: "Who was the first woman to win a Nobel Prize?", answer: "Marie Curie, who won the Nobel Prize in Physics in 1903.", category: "History", difficulty: "medium" },
  { question: "What year was the Declaration of Independence signed?", answer: "1776. It was adopted by the Continental Congress on July 4, 1776.", category: "History", difficulty: "easy" },
  { question: "Which empire was ruled by Genghis Khan?", answer: "The Mongol Empire, which became the largest contiguous land empire in history.", category: "History", difficulty: "easy" },
  { question: "What was the name of the ship that carried the Pilgrims to America in 1620?", answer: "The Mayflower, which departed from Plymouth, England.", category: "History", difficulty: "easy" },
  { question: "How long did the Hundred Years' War actually last?", answer: "116 years, from 1337 to 1453, fought between England and France.", category: "History", difficulty: "hard" },
  { question: "Who invented the printing press?", answer: "Johannes Gutenberg, around 1440, revolutionizing the spread of knowledge in Europe.", category: "History", difficulty: "medium" },
  { question: "What ancient wonder of the world is the only one still standing?", answer: "The Great Pyramid of Giza, built around 2560 BC in Egypt.", category: "History", difficulty: "medium" },
  { question: "What year did the Titanic sink?", answer: "1912. The RMS Titanic sank on April 15, 1912, after striking an iceberg.", category: "History", difficulty: "easy" },

  // Pop Culture
  { question: "What is the highest-grossing film of all time (not adjusted for inflation)?", answer: "Avatar (2009), directed by James Cameron, with over $2.9 billion worldwide.", category: "Pop Culture", difficulty: "medium" },
  { question: "Which band has sold the most records worldwide?", answer: "The Beatles, with estimated sales of over 600 million records.", category: "Pop Culture", difficulty: "easy" },
  { question: "What was the first feature-length animated film ever released?", answer: "El Apóstol (1917) from Argentina, though Snow White and the Seven Dwarfs (1937) is the first full-length cel-animated feature in motion picture history.", category: "Pop Culture", difficulty: "hard" },
  { question: "Who painted the Mona Lisa?", answer: "Leonardo da Vinci, painted between approximately 1503 and 1519.", category: "Pop Culture", difficulty: "easy" },
  { question: "What is the best-selling video game of all time?", answer: "Minecraft, with over 300 million copies sold across all platforms.", category: "Pop Culture", difficulty: "easy" },
  { question: "What TV show holds the record for most Emmy Awards?", answer: "Game of Thrones, with 59 Emmy Awards during its run.", category: "Pop Culture", difficulty: "medium" },
  { question: "Who wrote the Harry Potter book series?", answer: "J.K. Rowling. The first book, Harry Potter and the Philosopher's Stone, was published in 1997.", category: "Pop Culture", difficulty: "easy" },
  { question: "What is the most-streamed song on Spotify of all time?", answer: "\"Blinding Lights\" by The Weeknd, surpassing 4 billion streams.", category: "Pop Culture", difficulty: "medium" },
  { question: "Which movie won the first Academy Award for Best Picture?", answer: "Wings (1927), a silent film about World War I fighter pilots.", category: "Pop Culture", difficulty: "hard" },
  { question: "What is the longest-running animated TV series in the United States?", answer: "The Simpsons, which premiered on December 17, 1989, and is still airing.", category: "Pop Culture", difficulty: "easy" },
  { question: "Who is the most-followed person on Instagram?", answer: "Cristiano Ronaldo, with over 600 million followers.", category: "Pop Culture", difficulty: "medium" },
  { question: "What was the first toy ever advertised on television?", answer: "Mr. Potato Head, in 1952, becoming the first toy advertised on TV.", category: "Pop Culture", difficulty: "hard" },
  { question: "Which artist has won the most Grammy Awards?", answer: "Beyoncé, with 32 Grammy Awards as of 2024.", category: "Pop Culture", difficulty: "medium" },

  // Sports
  { question: "How many players are on a standard soccer (football) team on the field?", answer: "11 players per team on the field at one time.", category: "Sports", difficulty: "easy" },
  { question: "What country has won the most FIFA World Cup titles?", answer: "Brazil, with 5 World Cup titles (1958, 1962, 1970, 1994, 2002).", category: "Sports", difficulty: "easy" },
  { question: "What is the only sport to have been played on the Moon?", answer: "Golf. Alan Shepard hit two golf balls on the Moon during the Apollo 14 mission in 1971.", category: "Sports", difficulty: "hard" },
  { question: "Who holds the record for the most Olympic gold medals?", answer: "Michael Phelps, with 23 Olympic gold medals in swimming.", category: "Sports", difficulty: "easy" },
  { question: "How long is a marathon in kilometers?", answer: "42.195 kilometers (26.2 miles).", category: "Sports", difficulty: "medium" },
  { question: "In which sport would you perform a 'slam dunk'?", answer: "Basketball, where a player jumps and scores by pushing the ball directly through the hoop.", category: "Sports", difficulty: "easy" },
  { question: "What is the oldest Grand Slam tennis tournament?", answer: "Wimbledon, first held in 1877 in London, England.", category: "Sports", difficulty: "medium" },
  { question: "How many rings are on the Olympic flag?", answer: "Five rings, representing the five continents: Africa, the Americas, Asia, Europe, and Oceania.", category: "Sports", difficulty: "easy" },
  { question: "What is the fastest recorded tennis serve in history?", answer: "263.4 km/h (163.7 mph) by Sam Groth in 2012.", category: "Sports", difficulty: "hard" },
  { question: "Who scored the most goals in a single calendar year?", answer: "Lionel Messi, who scored 91 goals in 2012.", category: "Sports", difficulty: "medium" },
  { question: "What sport is known as 'the gentleman's game'?", answer: "Cricket, originating in England in the 16th century.", category: "Sports", difficulty: "medium" },
  { question: "In American football, how many points is a touchdown worth?", answer: "6 points, with the option for an extra point (1) or two-point conversion.", category: "Sports", difficulty: "easy" },
  { question: "What country invented the sport of judo?", answer: "Japan. Judo was created by Jigoro Kano in 1882.", category: "Sports", difficulty: "medium" },

  // Food & Drink
  { question: "What is the most consumed beverage in the world after water?", answer: "Tea, consumed by billions of people daily across the globe.", category: "Food", difficulty: "easy" },
  { question: "Which country is the world's largest producer of coffee?", answer: "Brazil, producing about one-third of the world's coffee supply.", category: "Food", difficulty: "easy" },
  { question: "What fruit is known as the 'king of fruits' in Southeast Asia?", answer: "Durian, famous for its strong smell and creamy texture.", category: "Food", difficulty: "medium" },
  { question: "What is the most expensive spice in the world by weight?", answer: "Saffron, which can cost up to $5,000 per pound due to labor-intensive harvesting.", category: "Food", difficulty: "medium" },
  { question: "Which country eats the most chocolate per capita?", answer: "Switzerland, consuming about 8.8 kilograms (19.4 pounds) per person annually.", category: "Food", difficulty: "medium" },
  { question: "What is the main ingredient in traditional Japanese miso soup?", answer: "Fermented soybean paste (miso), combined with dashi stock.", category: "Food", difficulty: "easy" },
  { question: "How many varieties of bananas exist in the world?", answer: "Over 1,000 varieties, though the Cavendish is the most commonly sold worldwide.", category: "Food", difficulty: "hard" },
  { question: "What country invented pizza as we know it?", answer: "Italy. The modern pizza originated in Naples in the 18th century.", category: "Food", difficulty: "easy" },
  { question: "What is the hottest chili pepper in the world?", answer: "Pepper X, measuring over 2.69 million Scoville Heat Units (SHU) as of 2023.", category: "Food", difficulty: "hard" },
  { question: "Which nut is used to make marzipan?", answer: "Almonds, ground and mixed with sugar and sometimes egg whites.", category: "Food", difficulty: "medium" },
  { question: "What is the most widely eaten food in the world?", answer: "Rice, serving as a staple food for over half of the world's population.", category: "Food", difficulty: "easy" },
  { question: "What gives bread its holes?", answer: "Carbon dioxide gas produced by yeast during fermentation, which gets trapped in the gluten network.", category: "Food", difficulty: "medium" },
  { question: "What country produces the most wine?", answer: "Italy, followed closely by France and Spain.", category: "Food", difficulty: "medium" },
]

const categories = ["All", "Geography", "Science", "History", "Pop Culture", "Sports", "Food"] as const

const difficultyStyles: Record<Difficulty, { bg: string; text: string; label: string }> = {
  easy: { bg: "bg-[#d7eee7]", text: "text-[#245e53]", label: "Easy" },
  medium: { bg: "bg-[#ffe0da]", text: "text-[#9f332a]", label: "Medium" },
  hard: { bg: "bg-[#f5d6d3]", text: "text-[#8f2922]", label: "Hard" },
}

const categoryStyles: Record<string, { bg: string; border: string; text: string }> = {
  Geography: { bg: "bg-[#e2edf4]", border: "border-[#9db9cc]", text: "text-[#315f7d]" },
  Science: { bg: "bg-[#e4ebf7]", border: "border-[#a6b9d8]", text: "text-[#365f98]" },
  History: { bg: "bg-[#ffe6df]", border: "border-[#e6aaa0]", text: "text-[#9f3a31]" },
  "Pop Culture": { bg: "bg-[#f7e1e4]", border: "border-[#dda9b2]", text: "text-[#984251]" },
  Sports: { bg: "bg-[#dcefe9]", border: "border-[#99c3b7]", text: "text-[#2e6b5e]" },
  Food: { bg: "bg-[#f4e6d8]", border: "border-[#d4b493]", text: "text-[#815733]" },
}

export function RandomTriviaTool() {
  const [activeCategory, setActiveCategory] = useState<string>("All")
  const [revealed, setRevealed] = useState(false)
  const [answeredCount, setAnsweredCount] = useState(0)
  const [usedIndices, setUsedIndices] = useState<Set<number>>(new Set())

  const filteredQuestions = useMemo(
    () =>
      activeCategory === "All"
        ? triviaQuestions
        : triviaQuestions.filter((q) => q.category === activeCategory),
    [activeCategory]
  )

  const getRandomIndex = useCallback(
    (excludeUsed: boolean = true) => {
      const available = filteredQuestions
        .map((_, i) => i)
        .filter((i) => !excludeUsed || !usedIndices.has(triviaQuestions.indexOf(filteredQuestions[i])))

      if (available.length === 0) {
        // All questions seen, reset
        setUsedIndices(new Set())
        return Math.floor(Math.random() * filteredQuestions.length)
      }
      return available[Math.floor(Math.random() * available.length)]
    },
    [filteredQuestions, usedIndices]
  )

  // A deterministic first question avoids a server/client hydration mismatch.
  const [currentIndex, setCurrentIndex] = useState(0)

  const currentQuestion = filteredQuestions[currentIndex % filteredQuestions.length]
  const difficulty = difficultyStyles[currentQuestion.difficulty]
  const catStyle = categoryStyles[currentQuestion.category] || categoryStyles.Geography

  const handleReveal = () => {
    if (!revealed) {
      if (answeredCount === 0) {
        trackProductEvent("tool_started", {
          properties: { tool_slug: "random-trivia-generator" },
        })
      }
      trackProductEvent("tool_completed", {
        properties: {
          tool_slug: "random-trivia-generator",
          interaction_type: "answer_revealed",
          category: currentQuestion.category,
          difficulty: currentQuestion.difficulty,
          interactions_completed: answeredCount + 1,
        },
      })
      setRevealed(true)
      setAnsweredCount((c) => c + 1)
      setUsedIndices((prev) => {
        const next = new Set(prev)
        next.add(triviaQuestions.indexOf(currentQuestion))
        return next
      })
    }
  }

  const handleNext = () => {
    setRevealed(false)
    const newIndex = getRandomIndex()
    setCurrentIndex(newIndex)
  }

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat)
    setRevealed(false)
    const filtered =
      cat === "All"
        ? triviaQuestions
        : triviaQuestions.filter((q) => q.category === cat)
    setCurrentIndex(Math.floor(Math.random() * filtered.length))
  }

  return (
    <div className="space-y-6">
      {/* Category filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-zinc-500 mr-1" />
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => handleCategoryChange(cat)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
              activeCategory === cat
                ? "bg-[#ff7569] text-[#171717] border border-[#b83f34]"
                : "bg-[#eee6db] text-[#514b44] border border-[#cfc3b5] hover:border-[#b83f34] hover:text-[#171717]"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Trivia card */}
      <div
        className={`relative rounded-2xl ${catStyle.bg} border ${catStyle.border} overflow-hidden transition-all duration-300`}
      >
        <div className="p-6 md:p-8">
          {/* Header with category and difficulty */}
          <div className="flex items-center justify-between mb-5">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${catStyle.text} ${catStyle.bg} border ${catStyle.border}`}>
              {currentQuestion.category}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${difficulty.text} ${difficulty.bg}`}>
              {difficulty.label}
            </span>
          </div>

          {/* Question */}
          <p className="text-[#171717] font-semibold text-xl md:text-2xl mb-6 leading-relaxed">
            {currentQuestion.question}
          </p>

          {/* Answer area */}
          <div
            className={`rounded-xl transition-all duration-300 overflow-hidden ${
              revealed
                ? "bg-[#171717] border border-[#171717] p-5"
                : "bg-white/55 border border-[#171717]/20 hover:border-[#171717]/55"
            }`}
          >
            {revealed ? (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <p className="text-sm font-bold text-[#ff8a80] mb-1">Answer</p>
                <p className="text-[#fffaf3] leading-relaxed">{currentQuestion.answer}</p>
              </div>
            ) : (
              <button
                onClick={handleReveal}
                className="w-full flex items-center justify-center gap-2 p-5 text-[#302d29] hover:text-[#171717] transition-colors cursor-pointer"
              >
                <Eye className="w-5 h-5" />
                <span className="font-medium">Reveal Answer</span>
              </button>
            )}
          </div>

          {/* Next button */}
          <button
            onClick={handleNext}
            className="coral-cta mt-4 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border text-[#171717] font-semibold cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            Next Question
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/45 border border-[#cfc3b5]">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-lg font-bold text-[#171717]">{answeredCount}</p>
            <p className="text-xs text-[#655d54]">Answered</p>
          </div>
          <div className="w-px h-8 bg-[#cfc3b5]" />
          <div className="text-center">
            <p className="text-lg font-bold text-[#171717]">{filteredQuestions.length}</p>
            <p className="text-xs text-[#655d54]">Available</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[#655d54]">
          <EyeOff className="w-3.5 h-3.5" />
          {filteredQuestions.length - usedIndices.size} remaining
        </div>
      </div>
    </div>
  )
}
